import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE } from '@/config/ogImages';
import { SUPPORTED_LANGUAGES, DEFAULT_LANG } from '@/i18n/config';
import { stripLang, getLangFromPath } from '@/i18n/useLocalizedPath';

/**
 * SEOHead Component
 * 
 * Manages all SEO meta tags including Open Graph for social sharing.
 * 
 * Usage:
 * <SEOHead
 *   title="Page Title"
 *   description="Page description for SEO and social sharing"
 *   url="https://events.jigsawconferences.co.uk/page"
 *   image={getOgImage('pageType', 'slug')} // Import from @/config/ogImages
 *   schema={jsonLdSchema} // Optional structured data
 * />
 * 
 * For new pages:
 * 1. Add OG image mapping in /src/config/ogImages.js
 * 2. Create the image file in /public/og/
 * 3. Pass the image prop using getOgImage() helper
 */
export function SEOHead({
  title,
  description,
  url,
  canonicalUrl,
  schema,
  image,
  robotsContent = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  includeHreflang = true,
  includeCanonical = true,
  includeSocialMeta = true,
}) {
  const defaultTitle = 'Jigsaw Events Hub | AI-Powered Event Intelligence | Free Accommodation';
  const defaultDescription = 'Discover B2B events, predict your ROI with AI, and get free group accommodation. Trusted by 640,000+ organisers since 2003. No booking fees.';
  const baseUrl = 'https://events.jigsawconferences.co.uk';
  const defaultImage = DEFAULT_OG_IMAGE;

  const location = useLocation();
  const currentLang = getLangFromPath(location.pathname);
  const cleanPath = stripLang(location.pathname);

  // Build an absolute URL for a given language version of the current page.
  const altUrl = (code) => {
    const pathPart = cleanPath === '/' ? '' : cleanPath;
    return code === DEFAULT_LANG ? `${baseUrl}${pathPart}` : `${baseUrl}/${code}${pathPart}`;
  };

  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  // Canonical is language-specific so each localized page is self-canonical
  // (English may still use an explicitly passed URL for backwards compatibility).
  const resolvedCanonicalUrl = canonicalUrl || (currentLang === DEFAULT_LANG ? (url || altUrl(DEFAULT_LANG)) : altUrl(currentLang));
  const finalUrl = resolvedCanonicalUrl;
  const finalImage = image || defaultImage;

  // hreflang alternates for all supported languages + x-default
  const hreflangs = [
    ...SUPPORTED_LANGUAGES.map((l) => ({ lang: l.code, href: altUrl(l.code) })),
    { lang: 'x-default', href: altUrl(DEFAULT_LANG) },
  ];

  // Force update of meta tags using DOM manipulation as a fallback
  useEffect(() => {
    // Update document title directly for better SPA support
    document.title = finalTitle;
    
    // Update existing meta description or create new one
    const ensureNamedMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    ensureNamedMeta('description', finalDescription);
    ensureNamedMeta('robots', robotsContent);
    ensureNamedMeta('googlebot', robotsContent);
    
    // Ensure OG tags exist
    const ensureOgTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Ensure Twitter tags exist
    const ensureTwitterTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Ensure canonical link exists
    let canonical = document.querySelector('link[rel="canonical"]');
    if (includeCanonical) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', finalUrl);
    } else if (canonical) {
      canonical.remove();
    }

    // Manage hreflang alternate links (remove stale, add current set)
    document.querySelectorAll('link[rel="alternate"][data-seo-hreflang="true"]').forEach((el) => el.remove());
    if (includeHreflang) {
      hreflangs.forEach(({ lang, href }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        link.setAttribute('href', href);
        link.setAttribute('data-seo-hreflang', 'true');
        document.head.appendChild(link);
      });
    }

    // Update OG/Twitter tags only when enabled
    if (includeSocialMeta) {
      ensureOgTag('og:title', finalTitle);
      ensureOgTag('og:description', finalDescription);
      ensureOgTag('og:url', finalUrl);
      ensureOgTag('og:type', 'website');
      ensureOgTag('og:image', finalImage);
      ensureOgTag('og:site_name', 'Jigsaw Conferences');

      ensureTwitterTag('twitter:card', 'summary_large_image');
      ensureTwitterTag('twitter:title', finalTitle);
      ensureTwitterTag('twitter:description', finalDescription);
      ensureTwitterTag('twitter:image', finalImage);
    } else {
      ['og:title', 'og:description', 'og:url', 'og:type', 'og:image', 'og:site_name'].forEach((property) => {
        const tag = document.querySelector(`meta[property="${property}"]`);
        if (tag) tag.remove();
      });
      ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach((name) => {
        const tag = document.querySelector(`meta[name="${name}"]`);
        if (tag) tag.remove();
      });
    }

    // Update JSON-LD if provided
    if (schema) {
      let jsonLdScript = document.querySelector('script[data-seo-jsonld="true"]');
      if (!jsonLdScript) {
        jsonLdScript = document.createElement('script');
        jsonLdScript.setAttribute('type', 'application/ld+json');
        jsonLdScript.setAttribute('data-seo-jsonld', 'true');
        document.head.appendChild(jsonLdScript);
      }
      jsonLdScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function to remove dynamically added tags on unmount
    return () => {
      // Remove JSON-LD on page change to prevent stale data
      const jsonLdScript = document.querySelector('script[data-seo-jsonld="true"]');
      if (jsonLdScript) {
        jsonLdScript.remove();
      }
      // Remove hreflang links on page change to prevent stale alternates
      document.querySelectorAll('link[rel="alternate"][data-seo-hreflang="true"]').forEach((el) => el.remove());
    };
  }, [finalTitle, finalDescription, finalUrl, finalImage, schema, robotsContent, includeHreflang, includeCanonical, includeSocialMeta, hreflangs]);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {includeCanonical && <link rel="canonical" href={finalUrl} />}
      {/* hreflang alternates are injected imperatively in the effect above to
          guarantee a single, de-duplicated set in <head>. */}
      {includeSocialMeta && <meta property="og:title" content={finalTitle} />}
      {includeSocialMeta && <meta property="og:description" content={finalDescription} />}
      {includeSocialMeta && <meta property="og:url" content={finalUrl} />}
      {includeSocialMeta && <meta property="og:type" content="website" />}
      {includeSocialMeta && <meta property="og:image" content={finalImage} />}
      {includeSocialMeta && <meta property="og:site_name" content="Jigsaw Conferences" />}
      {includeSocialMeta && <meta name="twitter:card" content="summary_large_image" />}
      {includeSocialMeta && <meta name="twitter:title" content={finalTitle} />}
      {includeSocialMeta && <meta name="twitter:description" content={finalDescription} />}
      {includeSocialMeta && <meta name="twitter:image" content={finalImage} />}
      {/* Brave Search / Claude AI optimization */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="author" content="Jigsaw Events Team" />
      <meta name="publisher" content="Jigsaw Conferences" />
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}